import {
  Content,
  KeyTextField,
  LinkField,
  RichTextField,
} from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  CardsComparisonSlice2Cards,
  CardsComparisonSliceDefault,
  CardsComparisonSliceFeatured,
  CardsComparisonSliceWithImages,
} from '../../../prismicio-types';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { TitleSection } from '@/components';

enum SLICE_VARIATION {
  twoCards = '2Cards',
  default = 'default',
  withImages = 'withImages',
  featured = 'featured',
}

/**
 * Props for `CardsComparison`.
 */
export type CardsComparisonProps =
  SliceComponentProps<Content.CardsComparisonSlice>;

/**
 * Component for "CardsComparison" Slices.
 */
const CardsComparison = ({ slice }: CardsComparisonProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-5 md:px-16 py-20 max-w-screen-2xl mx-auto"
    >
      {slice.variation === SLICE_VARIATION.twoCards && (
        <TwoCards slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.default && (
        <ThreeCards slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withImages && (
        <CardsWithImages slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.featured && (
        <FeaturedCards slice={slice} />
      )}
    </section>
  );
};

const FeaturedCards = ({ slice }: { slice: CardsComparisonSliceFeatured }) => {
  const {
    primary: { title, description, cards_style: cardsStylePrismic, cards },
  } = slice;

  const getCardStyle = (cardStyle: string) => {
    switch (cardStyle) {
      case 'black':
      default:
        return {
          container: 'card p-6 flex flex-col gap-y-4',
          content: '',
        };
      case 'white':
        return {
          container: 'bg-white-gradient',
          content: 'text-primary-darker-green-black',
        };
    }
  };

  return (
    <div className="text-white">
      <TitleSection title={title} description={description} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {cards.map((card, index) => (
          <div
            className={`rounded-[20px] last:md:col-span-2 last:lg:col-span-1 ${getCardStyle(cardsStylePrismic).container} ${card.image.url ? '' : 'p-8'}`}
            key={`card-featured-${index}`}
          >
            {card.icon && <PrismicNextImage field={card.icon} />}
            {card.image && (
              <PrismicNextImage
                field={card.image}
                className="rounded-t-[20px]"
              />
            )}
            <div
              className={`flex flex-col gap-y-8 ${getCardStyle(cardsStylePrismic).content} ${card.image.url ? 'p-8' : ''}`}
            >
              <PrismicRichText field={card.title} />
              <PrismicRichText field={card.description} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CardsWithImages = ({
  slice,
}: {
  slice: CardsComparisonSliceWithImages;
}): JSX.Element => {
  const {
    primary: { title, subtitle, cards },
  } = slice;
  return (
    <div className="text-white">
      <TitleSection title={title} description={subtitle} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {cards.map(
          (
            {
              title,
              description,
              button_label: buttonLabel,
              button_link: buttonLink,
              image,
            },
            index,
          ) => (
            <div key={`card-${index}`} className="card flex-col">
              <div className="p-6 md:p-12 flex flex-col gap-y-6">
                <PrismicRichText field={title} />
                <PrismicRichText field={description} />
                <PrismicNextLink
                  field={buttonLink}
                  className="button-outline-primary"
                >
                  <span>{buttonLabel}</span>
                </PrismicNextLink>
              </div>
              <PrismicNextImage field={image} className="rounded-b-[20px]" />
            </div>
          ),
        )}
      </div>
    </div>
  );
};

const ThreeCards = ({
  slice,
}: {
  slice: CardsComparisonSliceDefault;
}): JSX.Element => {
  const {
    primary: {
      footer_message: footerMessage,
      button_label: buttonLabel,
      button_link: buttonLink,
      cards,
      title,
      description,
    },
  } = slice;
  return (
    <div className="text-white">
      <TitleSection title={title} description={description} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {cards.map(({ title, list_items: listItems }, index) => (
          <div
            key={`card-${index}`}
            className="card p-8 md:last:col-span-2 lg:last:col-span-1"
          >
            <PrismicRichText field={title} />
            <Divider bgColor="bg-button-gradient" />
            <PrismicRichText
              field={listItems}
              components={{
                list: ({ children }) => (
                  <ul className="checklist">{children}</ul>
                ),
              }}
            />
          </div>
        ))}
      </div>
      <FooterSection
        footerMessage={footerMessage}
        buttonLabel={buttonLabel}
        buttonLink={buttonLink}
      />
    </div>
  );
};

const TwoCards = ({
  slice,
}: {
  slice: CardsComparisonSlice2Cards;
}): JSX.Element => {
  const {
    primary: {
      title,
      subtitle,
      cards,
      footer_message: footerMessage,
      button_label: buttonLabel,
      button_link: buttonLink,
    },
  } = slice;
  return (
    <div className="text-white">
      <TitleSection title={title} description={subtitle} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map(
          (
            {
              title,
              button_label: buttonLabel,
              button_link: buttonLink,
              list_items: listItems,
            },
            index,
          ) => (
            <div key={`card-${index}`} className="card p-8 text-center">
              <div className={index === 1 ? 'text-[#05F273]' : ''}>
                <PrismicRichText field={title} />
              </div>
              <Divider bgColor="bg-button-gradient" />
              {buttonLabel && (
                <PrismicNextLink
                  field={buttonLink}
                  className="button-bordered w-full md:w-1/2 text-center"
                >
                  <span>{buttonLabel}</span>
                </PrismicNextLink>
              )}
              <PrismicRichText
                field={listItems}
                components={{
                  list: ({ children }) => (
                    <ul className="checklist">{children}</ul>
                  ),
                }}
              />
            </div>
          ),
        )}
      </div>
      <FooterSection
        footerMessage={footerMessage}
        buttonLink={buttonLink}
        buttonLabel={buttonLabel}
      />
    </div>
  );
};

const FooterSection = ({
  footerMessage,
  buttonLink,
  buttonLabel,
}: {
  footerMessage: RichTextField;
  buttonLink: LinkField;
  buttonLabel: KeyTextField;
}): JSX.Element => {
  return (
    <div className="flex flex-col items-center mt-12 gap-y-12">
      <PrismicRichText
        field={footerMessage}
        components={{
          paragraph: ({ children }) => (
            <p className="text-medium-normal">{children}</p>
          ),
        }}
      />
      <PrismicNextLink field={buttonLink} className="button-bordered">
        <span>{buttonLabel}</span>
      </PrismicNextLink>
    </div>
  );
};

const Divider = ({ bgColor }: { bgColor: string }): JSX.Element => {
  return <div className={`w-full h-[1px] ${bgColor}`}></div>;
};

export default CardsComparison;
